
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  ETL_STATUS_DATE_FORMAT,
  EtlStatusRow,
  fetchEtlStatuses,
  reEtlResource,
  validateReEtlFromTime,
} from '@/models/etlStatus';

const DEFAULT_RESOURCE = 'products';

@Component
export default class EtlStatusDialog extends Vue {
  @Prop({ required: true }) public value!: boolean;
  @Prop({ required: true }) public storeId!: number;

  public rows: EtlStatusRow[] = [];
  public selectedId: number | null = null;
  public fromTime: string = '';
  public triggerNow: boolean = true;
  public loading: boolean = false;
  public saving: boolean = false;
  public successMessage: string = '';
  public warningMessage: string = '';
  public errorMessage: string = '';
  public dateFormat: string = ETL_STATUS_DATE_FORMAT;

  get show() {
    return this.value;
  }

  set show(value: boolean) {
    this.$emit('input', value);
  }

  get resourceItems(): Array<{ text: string; value: number }> {
    return this.rows.map((row) => ({ text: row.resource, value: row.id }));
  }

  get selectedRow(): EtlStatusRow | null {
    return this.rows.find((row) => row.id === this.selectedId) || null;
  }

  get canSave(): boolean {
    return !!this.selectedRow && !this.saving && this.fromTimeRule(this.fromTime) === true;
  }

  public fromTimeRule(value: string): boolean | string {
    return this.selectedRow ? validateReEtlFromTime(this.selectedRow, value) : true;
  }

  @Watch('show', { immediate: true })
  public onShowChanged(visible: boolean) {
    if (visible) {
      this.load();
    }
  }

  @Watch('selectedId')
  public onSelectedChanged() {
    this.fromTime = '';
  }

  public async load() {
    this.loading = true;
    this.clearMessages();
    try {
      this.rows = await fetchEtlStatuses(this.storeId);
      if (!this.selectedRow) {
        const preferred = this.rows.find((row) => row.resource === DEFAULT_RESOURCE) || this.rows[0];
        this.selectedId = preferred ? preferred.id : null;
      }
    } catch (err) {
      this.errorMessage = this.errorText(err, 'Failed to load ETL statuses');
    } finally {
      this.loading = false;
    }
  }

  public async save() {
    const row = this.selectedRow;
    if (!row || !this.canSave) {
      return;
    }
    const fromTime = this.fromTime.trim();
    const runText = this.triggerNow ? 'and run it now' : 'on the next scheduled ETL';
    const confirmed = window.confirm(
      `Re-ETL ${row.resource} of store #${this.storeId} from ${fromTime} (UTC) ${runText}?`,
    );
    if (!confirmed) {
      return;
    }

    this.saving = true;
    this.clearMessages();
    try {
      const result = await reEtlResource(this.storeId, row.id, fromTime, this.triggerNow);
      this.rows = this.rows.map((item) => (item.id === result.etlStatus.id ? result.etlStatus : item));
      if (result.triggerError) {
        this.warningMessage = `Watermark moved to ${fromTime}, but the run could not be queued `
          + `(${result.triggerError}). The next scheduled ETL will pick it up.`;
      } else {
        this.successMessage = result.triggered
          ? `Re-ETL of ${row.resource} from ${fromTime} queued.`
          : `Watermark moved to ${fromTime}. The next scheduled ETL will pick it up.`;
      }
      this.$emit('updated', result.etlStatus);
    } catch (err) {
      this.errorMessage = this.errorText(err, 'Failed to re-ETL resource');
    } finally {
      this.saving = false;
    }
  }

  public close() {
    this.show = false;
  }

  private clearMessages() {
    this.successMessage = '';
    this.warningMessage = '';
    this.errorMessage = '';
  }

  private errorText(err: any, fallback: string): string {
    const data = err && err.response && err.response.data;
    const parsed = typeof data === 'string' ? this.safeParse(data) : data;
    return (parsed && parsed.message) || fallback;
  }

  private safeParse(text: string): any {
    try {
      return JSON.parse(text);
    } catch (e) {
      return null;
    }
  }
}
